import React, { useState, useEffect } from 'react';
import { X, Clock, User, FileText, CheckCircle, XCircle, Edit, Trash2, Send, List, Activity } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import AuditTimeline from './AuditTimeline';

export default function AuditTrailModal({ collectionId, collectionName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [viewMode, setViewMode] = useState('timeline'); // Default to timeline view

  useEffect(() => {
    fetchAuditTrail();
  }, [collectionId]);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/audit/proposal/${collectionId}`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'approve':
      case 'approved':
      case 'bulk_approve':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'reject':
      case 'rejected':
      case 'bulk_reject':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'create':
      case 'created':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'update':
      case 'updated':
      case 'edit':
        return <Edit className="w-5 h-5 text-orange-600" />;
      case 'delete':
      case 'deleted':
        return <Trash2 className="w-5 h-5 text-red-600" />;
      case 'submit':
      case 'submitted':
        return <Send className="w-5 h-5 text-indigo-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActionColor = (action) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('approve')) return 'bg-green-50 border-green-200 text-green-700';
    if (lowerAction.includes('reject')) return 'bg-red-50 border-red-200 text-red-700';
    if (lowerAction.includes('create')) return 'bg-blue-50 border-blue-200 text-blue-700';
    if (lowerAction.includes('update') || lowerAction.includes('edit')) return 'bg-orange-50 border-orange-200 text-orange-700';
    if (lowerAction.includes('delete')) return 'bg-red-50 border-red-200 text-red-700';
    if (lowerAction.includes('submit')) return 'bg-indigo-50 border-indigo-200 text-indigo-700';
    return 'bg-gray-50 border-gray-200 text-gray-700';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Audit Trail</h2>
            <p className="text-sm text-gray-600 mt-1">{collectionName}</p>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1 mr-4">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'table' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Table View"
            >
              <List className="w-4 h-4" />
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode('timeline')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'timeline' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Timeline View"
            >
              <Activity className="w-4 h-4" />
              Timeline
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No audit trail records found for this proposal.</p>
            </div>
          ) : viewMode === 'timeline' ? (
            <AuditTimeline entries={logs} showEntityInfo={false} />
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div
                  key={log.id || index}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getActionIcon(log.action)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </div>

                      {/* User Info */}
                      <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                        <User className="w-4 h-4" />
                        <span className="font-medium">
                          {log.user_display_name || log.user_name || log.user_email || 'Unknown User'}
                        </span>
                      </div>

                      {/* Description */}
                      {log.details?.description && (
                        <p className="text-sm text-gray-700 mt-2">
                          {log.details.description}
                        </p>
                      )}

                      {/* Additional Details */}
                      {log.details && Object.keys(log.details).length > 1 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md text-xs">
                          {Object.entries(log.details)
                            .filter(([key]) => key !== 'description')
                            .map(([key, value]) => (
                              <div key={key} className="flex gap-2 mb-1">
                                <span className="font-semibold text-gray-600">{key}:</span>
                                <span className="text-gray-700">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
